import PropTypes from "prop-types"
import React from "react"
import "./button-v2.scss"

/**
 * Utility function to transform button props into CSS classes
 * Centralized logic for converting buttonStyle and buttonSize to CSS classes
 */
export const getButtonClasses = (buttonStyle, buttonSize) => {
  const classes = []

  // Transform button style
  if (buttonStyle) {
    const style = Array.isArray(buttonStyle) ? buttonStyle[0] : buttonStyle
    if (style === "Solid Green") {
      classes.push("btn-v2--primary-green")
    } else if (style === "Solid Blue") {
      classes.push("btn-v2--primary-blue")
    } else if (style === "Solid Pastel Blue") {
      classes.push("btn-v2--primary-pastel-blue")
    } else if (style === "Outline Green") {
      classes.push("btn-v2--secondary-green")
    } else if (style === "Outline Blue") {
      classes.push("btn-v2--secondary-blue")
    } else if (style === "primary-green") {
      classes.push("btn-v2--primary-green")
    } else if (style === "primary-blue") {
      classes.push("btn-v2--primary-blue")
    } else if (style === "secondary-green") {
      classes.push("btn-v2--secondary-green")
    } else if (style === "secondary-blue") {
      classes.push("btn-v2--secondary-blue")
    }
  }

  // Transform button size
  if (buttonSize) {
    const size = Array.isArray(buttonSize) ? buttonSize[0] : buttonSize
    if (size === "Small") {
      classes.push("btn-v2--small")
    } else if (size === "Large") {
      classes.push("btn-v2--large")
    } else if (size === "small") {
      classes.push("btn-v2--small")
    } else if (size === "large") {
      classes.push("btn-v2--large")
    }
  }

  return classes.join(" ")
}

/**
 * ButtonV2 - A versatile button component that can render as a button or link
 * Supports opening links in new windows/tabs and various button types
 */
const ButtonV2 = ({
  className = "btn-v2",
  disabled = false,
  href,
  newWindow = false,
  onClick,
  showArrow = false,
  text,
  type = "button",
  ...props
}) => {
  // If href is provided, render as anchor tag
  if (href) {
    return (
      <a
        className={"btn-v2 " + className}
        href={href}
        rel={newWindow ? "noopener noreferrer" : undefined}
        target={newWindow ? "_blank" : "_self"}
        {...props}
      >
        <span className="btn-v2--text">{text}</span>
        {showArrow && (
          <svg
            aria-hidden="true"
            className="btn-v2--arrow"
            fill="none"
            height="20"
            viewBox="0 0 20 20"
            width="20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.002 4.167 15.835 10m0 0-5.833 5.834M15.835 10H4.168"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.663"
            />
          </svg>
        )}
        <span className="btn-v2--hover-bg" role="presentation"></span>
      </a>
    )
  }

  // Otherwise render as button
  return (
    <button
      className={"btn-v2 " + className}
      disabled={disabled}
      onClick={onClick}
      type={type}
      {...props}
    >
      <span className="btn-v2--text">{text}</span>
      {showArrow && (
        <svg
          aria-hidden="true"
          className="btn-v2--arrow"
          fill="none"
          height="20"
          viewBox="0 0 20 20"
          width="20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.002 4.167 15.835 10m0 0-5.833 5.834M15.835 10H4.168"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.663"
          />
        </svg>
      )}
      <span className="btn-v2--hover-bg" role="presentation"></span>
    </button>
  )
}

ButtonV2.propTypes = {
  // CSS classes
  className: PropTypes.string,
  // Disabled state
  disabled: PropTypes.bool,
  // URL for anchor tag rendering
  href: PropTypes.string,
  // HTML id
  id: PropTypes.string,
  // Open in new window
  newWindow: PropTypes.bool,
  // Click handler
  onClick: PropTypes.func,
  // Show arrow icon
  showArrow: PropTypes.bool,
  // Button text
  text: PropTypes.string.isRequired,
  // Button type
  type: PropTypes.oneOf(["button", "submit", "reset"]),
}

export default ButtonV2
